// ─────────────────────────────────────────────
//  secondProviderWrite — putting a second provider in a room, and taking one
//  out, from the two surfaces that can now do it: the timeline's quick-actions
//  menu and the room modal.
//
//  ⚠️ THE FIELD NAMES ARE NOT DECIDED HERE. `domain/secondProvider` owns them
//  (`secondProviderFields`, `CLEARED_SECOND_PROVIDER`) and owns the ONE resolver
//  (`secondProviderOf`). This module composes those with the two things a WRITE
//  needs that a read does not: the seat cell, and the shape a timeline case
//  arrives in.
//
//  ⚠️ THE GOTCHA THAT MAKES THIS FILE EXIST. Clearing `orientee*` IS NOT ENOUGH
//  on a legacy row. `orientationOf` falls back to PARSING `ahpName`, which on
//  every board already in Firestore still reads "Trevor Stamand 5 orient Kayla
//  Gibbons". Remove Kayla and she is re-derived out of that string on the next
//  render; put Grace there instead and the seat goes on printing the old
//  sentence, because `orientationOf`'s structured branch hands back `ahpName`
//  RAW as the preceptor's name. So a write that changes who the second person
//  is must also normalise the seat cell down to the one person holding the room.
// ─────────────────────────────────────────────
import { parseOrientation, seatName } from "./orientation";
import {
  secondProviderFields,
  secondProviderOf,
  CLEARED_SECOND_PROVIDER,
  SECOND_PROVIDER_ROLES,
} from "./secondProvider";
import { normalizeName } from "../features/team-directory/normalizeName";
// The ONE splitter for "a name with a shift token stuck to it". `domain/providerCell`
// owns that vocabulary; a private regex here would be a second definition of what a
// shift code looks like, and would drift the first time the sheet invents another.
import { splitNameAndShift } from "./providerCell";

// The name of the person HOLDING the room.
//
// ⚠️ ALWAYS FROM THE RAW CELL. `seatName` short-circuits on `orienteeName` and
// its structured branch returns `ahpName` untouched — so a legacy row that has
// just been given structured fields starts printing the whole sentence as the
// preceptor's name. Dropping the structured field before asking forces the
// parse path, which is the one that splits.
export function seatCellName(row) {
  return seatName({ ahpName: row?.ahpName || "" });
}

// Rewrite `ahpName` down to the seat, but ONLY when the cell really parses as a
// pair. An ordinary name is left byte-identical, so 17 of 19 rooms never see a
// write they did not ask for.
//
// The trailing shift digits ("Trevor Stamand 5") go with it: every surface
// already PRINTS the stripped name — `seatName` is what the bar label, the
// staffing card and the bench all read — and the real code lives in
// `ahpShiftCode`, which this never touches.
function withNormalisedSeat(row, fields) {
  const raw = String(row?.ahpName || "");
  if (!parseOrientation(raw)) return { ...fields };
  return { ...fields, ahpName: seatCellName(row) };
}

// Put somebody in the room as the second provider, or swap the one who is there.
//
// `role` is OPTIONAL and omitting it is meaningful: the patch then leaves
// `secondProviderRole` alone, so replacing a helping CRNA with another does not
// silently relabel the room as an orientation. Pass it only when the caller
// actually knows the relationship — the quick-actions menu does ("helping"),
// a Replace inside the room modal does not.
export function setSecondProviderPatch(row, person, { role } = {}) {
  const fields = secondProviderFields({
    id: person?.id || null,
    name: person?.name || "",
    shiftCode: person?.shiftCode ?? null,
    ...(role ? { role } : {}),
  });
  const patch = { ...fields };
  if (!role) delete patch.secondProviderRole;
  return withNormalisedSeat(row, patch);
}

// Change WHEN the second provider leaves, and nothing else.
//
// ⚠️ THE NARROWEST PATCH IN THIS FILE, ON PURPOSE. The timeline's lower lane
// drags this one value; `secondProviderFields` would also rewrite the id, the
// name, the shift code and the role, so using it here would let a drag on a
// leaving time silently relabel a helping CRNA as somebody's trainee, or blank
// the shift code the departure was originally derived from.
//
// ⚠️ AND THE SEAT CELL IS LEFT ALONE. `withNormalisedSeat` exists because
// changing WHO the second person is leaves `ahpName` still reading the whole
// "Trevor Stamand 5 orient Kayla Gibbons" sentence. Nobody changes here, so
// there is nothing to normalise — and rewriting `ahpName` on a legacy row as a
// side effect of moving a time would be a write the operator never asked for.
//
// `orienteeDepartTime` is the FIRST thing `timeline.orienteeDepartMinutes`
// reads, ahead of the shift code and ahead of the roster, so this works
// unchanged on a legacy row that carries no structured second-provider fields
// at all.
export function setSecondDepartPatch(departTime) {
  return { orienteeDepartTime: String(departTime || "") };
}

// The SAME edit, for the person who HOLDS the room.
//
// ⚠️ EQUALLY NARROW, FOR THE IDENTICAL REASON. The seat's full field set is
// `{ ahpId, ahpName, ahpShiftCode, ahpDepartTime }` and every other writer in
// the app sets all four together, because they are all changing WHO is in the
// seat. This one changes WHEN they leave and must therefore touch exactly one
// field: writing `ahpName` as a side effect of a time edit would re-flatten a
// legacy two-person cell, and writing `ahpShiftCode: null` would blank the code
// the departure was originally derived from.
//
// ⚠️ AND `ahpDepartTime` IS THE FIRST THING `timeline.crnaDepartMinutes` READS,
// ahead of the shift code — so this works on a row nobody ever gave a code to,
// which is exactly the row a human is editing by hand.
export function setSeatDepartPatch(departTime) {
  return { ahpDepartTime: String(departTime || "") };
}

// One call site, one slot, chosen by the caller's already-guarded position.
//
// ⚠️ THE SLOT IS THE WHOLE DECISION. The two fields are indistinguishable in
// shape, so a surface that picks the wrong one writes a plausible time against
// the wrong human being and nothing anywhere reports an error. It is spelled
// once, here, rather than in an `isSecond ? … : …` at the point of the write.
export function setDepartPatch({ second = false, departTime } = {}) {
  return second ? setSecondDepartPatch(departTime) : setSeatDepartPatch(departTime);
}

// What the row actually RECORDS as this person's leaving time — "" when nobody
// ever recorded one.
//
// ⚠️ THE STORED FIELD, NEVER THE DERIVED MINUTE. `timeline` publishes
// `departMin` / `depart12h` for both people, and on most rows those come from a
// shift code or from today's roster rather than from anything a human typed.
// Seeding an editor with a derived time offers a borrowed number as if it were
// recorded — and the moment the operator presses save, it becomes one. Absence
// has to look like absence.
export function recordedDepartTime(row, { second = false } = {}) {
  if (!row) return "";
  return String((second ? row.orienteeDepartTime : row.ahpDepartTime) || "").trim();
}

// May a write be keyed by this provider's id?
//
// ⚠️ A TRUTHY ID IS NOT A RESOLVED ONE, AND THAT IS THE TRAP. A board imported
// before the second-provider fields existed names both people in one cell and
// resolves neither; `secondProviderMember` still has to hand the roster an
// `id`, because `departed[undefined]` collides for every such person, so it
// mints the synthetic `sp:<name>` key `secondProviderOf` publishes. It is
// truthy, it is stable, and every roster / transfer / assignment write keyed by
// it lands on a document that does not exist — silently, because Firestore is
// happy to be told about an id nobody has heard of.
//
// `derived: true` is the same fact carried by the roster member, and is checked
// too so a caller holding a member rather than a raw resolver result gets the
// same answer.
export const SYNTHETIC_PROVIDER_PREFIX = "sp:";

export function isResolvedProvider(provider) {
  if (!provider || provider.derived === true) return false;
  const id = String(provider.id || "").trim();
  if (!id) return false;
  return !id.startsWith(SYNTHETIC_PROVIDER_PREFIX);
}

// ── RESOLVING A PERSON THE IMPORTER NEVER IDENTIFIED ─────────────────────
//
// ⚠️ A MISSING ID IS NOT AN ABSENT PERSON, AND THAT IS THE WHOLE DEFECT THIS
// SECTION EXISTS FOR. `isResolvedProvider` above answers "may a write be keyed
// by THIS id"; it was answering "no" for Taylor Prentice, Kayla Gibbons,
// Brianna Michaud, Josh Larson and Trevor Stamand — five real CRNAs who all
// have `staff` records — so the quick-actions menu shrank to one option for
// them while Mike Peterson kept all five. The owner's report was exactly that:
// *"why are they classified diferent, please fix it … all the crna need to have
// the same privileges"*.
//
// ⚠️ THE COMMON FACTOR IS THE CELL SHAPE, NOT THE PERSON. `"Mike Peterson 5"`
// ends in a shift token the importer knows, so it matched a staff record and the
// row stored an `ahpId`. `"Taylor Prentice 7 REQ"`, `"Josh Larson 3 Interview
// at"` and `"Trevor Stamand 5 orient Kayla Gibbons"` did not, so those rows
// carry `ahpId: null` / `orienteeId: null` and the clinician is named in plain
// text and nowhere else. Refusing them the menu treats an IMPORT failure as a
// fact about the person.
//
// So: look the name up in the pool the page already holds, and when exactly one
// real staff record answers to it, that IS the person — write with their id.
//
// ⚠️ THE JOIN KEY IS `features/team-directory/normalizeName`, NOT A NEW ONE.
// That function is this app's tested answer to "the sheet and the staff pool
// spell this person differently", and its header states the line it will not
// cross: it folds punctuation, accents, parentheticals and trailing
// shift/annotation tokens, and it NEVER touches letters — so it can fold
// "Josh Larson 7 REQ" onto "Josh Larson" but can never bring "Mitch O'Hara" and
// "Mitchell O'Hara" together. A fourth private copy of that vocabulary would
// drift from it, and the drift would surface as clinicians silently gaining or
// losing a menu.
export const IDENTITY_RESOLVED = "resolved";
export const IDENTITY_UNMATCHED = "unmatched";
export const IDENTITY_AMBIGUOUS = "ambiguous";

// How much bookkeeping a raw name carries beyond the person inside it:
// "Josh Larson" → 0, "Josh Larson 7 REQ" → 2. Used ONLY to break a tie between
// records already known to share one normalised name.
function annotationWeight(person) {
  const raw = String(person?.name || "").trim();
  if (!raw) return Number.MAX_SAFE_INTEGER;
  const rawTokens = raw.split(/\s+/).filter(Boolean).length;
  const keyTokens = normalizeName(raw).split(" ").filter(Boolean).length;
  return Math.max(0, rawTokens - keyTokens);
}

/**
 * Find the ONE staff record a bare name belongs to.
 *
 * Returns `{ status, person, matches }`:
 *   resolved  — exactly one record answers (after the tie-breaks below); a write
 *               may be keyed by `person.id`.
 *   unmatched — nobody answers. The caller keeps the reduced menu, which is the
 *               correct answer: there is no record for a write to land on.
 *   ambiguous — several answer and the tie-breaks could not separate them.
 *               ⚠️ THE CALLER MUST NOT GUESS. Writing one clinician's assignment
 *               onto another clinician's record is the worst outcome available
 *               on this screen, and strictly worse than a one-item menu.
 *
 * ⚠️ ONLY RECORDS THAT PASS `isResolvedProvider` ARE CANDIDATES. `crnaPool`
 * contains the DERIVED roster members `secondProviderMember` mints for exactly
 * the people this function is trying to resolve — same name, synthetic `sp:`
 * id, `derived: true`. Matching one of those would hand a write the very
 * phantom key this module exists to refuse, and it would look like a success.
 */
export function resolveProviderByName(name, pool = []) {
  const key = normalizeName(name);
  if (!key) return { status: IDENTITY_UNMATCHED, person: null, matches: [] };

  // Deduped by id: the same staff record reaches this function twice when the
  // caller concatenates today's roster with the staff master, and two copies of
  // one person are not an ambiguity.
  const byId = new Map();
  for (const p of Array.isArray(pool) ? pool : []) {
    if (!isResolvedProvider(p)) continue;
    if (normalizeName(p.name) !== key) continue;
    if (!byId.has(p.id)) byId.set(p.id, p);
  }
  const matches = [...byId.values()];
  if (matches.length === 0) return { status: IDENTITY_UNMATCHED, person: null, matches };
  if (matches.length === 1) return { status: IDENTITY_RESOLVED, person: matches[0], matches };

  // ── TWO TIE-BREAKS, AND THEN A REFUSAL ─────────────────────────────────
  //
  // Duplicates in this tenant's `staff` are not hypothetical: the importer used
  // to mint a record per annotated variant, so one human exists as "Josh
  // Larson", "Josh Larson 7 REQ" and more (pt.157 merged 14 of them in
  // production, and two hyphen-variants survived).
  //
  // 1. A `uid` means somebody has actually SIGNED IN as that record. No phantom
  //    minted from a schedule cell has one, so it is the strongest evidence
  //    available that a record belongs to a person rather than to a mis-parse.
  const withUid = matches.filter((p) => String(p?.uid || "").trim());
  const tier = withUid.length > 0 ? withUid : matches;
  if (tier.length === 1) return { status: IDENTITY_RESOLVED, person: tier[0], matches };

  // 2. The cleanest spelling, because the variants ARE the annotation: between
  //    "Josh Larson" and "Josh Larson 7 REQ" the first is the record a human
  //    created and the second is one the importer invented out of a cell.
  const min = Math.min(...tier.map(annotationWeight));
  const cleanest = tier.filter((p) => annotationWeight(p) === min);
  if (cleanest.length === 1) return { status: IDENTITY_RESOLVED, person: cleanest[0], matches };

  // ⚠️ STILL TIED = TWO DIFFERENT PEOPLE WHO SHARE A NAME, spelled equally
  // cleanly. Nothing here could separate them and the app must not invent
  // something that does. Refuse — and let the caller SAY it refused.
  return { status: IDENTITY_AMBIGUOUS, person: null, matches };
}

/**
 * The identity behind ONE name on a case bar — the question the quick-actions
 * menu has to answer before it decides what it may offer.
 *
 * ⚠️ THE ROW'S OWN ID WINS WHENEVER IT IS USABLE. A row that already identifies
 * somebody is never re-derived from its text, so no name lookup can move a
 * person whose record was already known. Only a row that identifies NOBODY —
 * `ahpId: null` on a seat, `orienteeId: null` on a second provider — reaches
 * the lookup, and BOTH slots reach it by the same path, because an orienting
 * pair loses both ids in one cell and both halves are equally real people.
 */
export function resolveBarIdentity({ id = null, name = "" } = {}, pool = []) {
  if (isResolvedProvider({ id })) {
    return { status: IDENTITY_RESOLVED, id, person: findById(pool, id), matches: [] };
  }
  const found = resolveProviderByName(name, pool);
  return { ...found, id: found.status === IDENTITY_RESOLVED ? found.person.id : null };
}

// Local, tolerant id lookup — a pool entry answers to its staff id OR to the
// login uid an older assignment row may carry instead. Same rule as
// `domain/staff.findInPool`, spelled here so a write-safety module does not
// have to pull in the board's pool vocabulary.
function findById(pool, id) {
  if (!id) return null;
  return (Array.isArray(pool) ? pool : []).find((p) => p && (p.id === id || p.uid === id)) || null;
}

// Take the second provider out of the room. Nothing else moves: the seat still
// holds the room, and where that person goes next (bench / home) is a separate
// decision made by whoever asked for this.
export function clearSecondProviderPatch(row) {
  return withNormalisedSeat(row, CLEARED_SECOND_PROVIDER);
}

// The preceptor steps out; the trainee keeps the room.
//
// ⚠️ THIS IS AN OVERRIDE, NOT THE RESTING STATE. Owner, 2026-09-07: *"maintain
// the preceptor in the room dont place in free now… the preceptor only is
// helping the orientee to manage the room. the supervisor is over both."* So an
// orienting pair leaves the ratio exactly as it is and the seat stays the
// preceptor — until a human presses the control that calls this, which is the
// moment that stops being true.
//
// The preceptor is not written ANYWHERE. `benchProviders` is
// roster-minus-assigned, so removing them from the seat is what puts them on
// FREE NOW; a second write naming them would be a second definition of what
// "free" means.
//
// ⚠️ AND THE ORIENTATION ENDS. `CLEARED_SECOND_PROVIDER` takes
// `secondProviderRole` with it — the trainee is now holding the room, which is
// not what an orientee is.
//
// Returns null — no write at all — rather than half-doing it, in three cases:
// the row is not a pair, the second person is a HELPING colleague (there is no
// preceptor to step out of a pair that was never a teaching relationship), or
// the trainee resolved to no staff record. The last one matters most: a seat
// holder with no id is invisible to every count keyed on id, so a name that
// matched nothing must not be promoted into the seat. The caller surfaces the
// reason; a control that silently does nothing reads as broken.
export function promotePreceptorOutPatch(row) {
  const sp = secondProviderOf(row);
  if (!sp || !sp.name) return null;
  if (sp.role !== SECOND_PROVIDER_ROLES.ORIENTING) return null;
  if (!sp.id) return null;
  return {
    ahpId: sp.id,
    ahpName: sp.name,
    ahpShiftCode: sp.shiftCode ?? null,
    // ⚠️ THE ORIENTEE'S OWN HOUR, AND ITS ABSENCE. `recordedDepartTime` returns
    // the STORED field, so a trainee nobody gave a leaving time carries "" into
    // the seat instead of inheriting the preceptor's — two people in one room
    // leave at two different times, which is why the second field exists.
    ahpDepartTime: recordedDepartTime(row, { second: true }),
    ...CLEARED_SECOND_PROVIDER,
  };
}

// The two people in the room trade places: the second provider takes the seat
// and the seat holder becomes the second provider.
//
// Owner, 2026-09-10: *"we can swap now, but i want to have a way to change the
// names and the position directly fron the box"* — the sheet writes "A orient B"
// and is sometimes the wrong way round, so the ORIENTEES card needs a correction
// that does not require re-typing either name.
//
// ⚠️ NOT `promotePreceptorOutPatch`. That one ENDS the pair: the preceptor
// leaves the room and the second slot is cleared. Here nobody leaves — both
// people are still in the room afterwards, so there is no departure, nothing to
// stamp free, and the relationship survives.
//
// ⚠️ EACH PERSON'S HOUR AND SHIFT CODE TRAVEL WITH THEM. Swapping only the names
// would tell the board that whoever now holds the room leaves at the other's
// time. Two people in one room leave at two different times — that is why the
// second set of fields exists at all.
//
// ⚠️ THE RELATIONSHIP IS PRESERVED, NEVER RE-DECIDED. Orienting stays orienting
// and helping stays helping: who is teaching whom is a different decision with
// its own control, and reordering two people must not smuggle it in.
//
// Returns null when the swap cannot be made, so a card can ask rather than
// offering a button the write would refuse.
export function swapPairPatch(row) {
  const sp = secondProviderOf(row);
  if (!sp || !sp.name) return null;
  // ⚠️ THE SAME FAIL-CLOSED RULE AS THE PROMOTE. The second person is about to
  // become the SEAT, and a seat keyed by the synthetic `sp:` id is a write onto
  // a document nobody has — silently, because Firestore accepts any id.
  if (!isResolvedProvider(sp)) return null;

  const seatId = row?.ahpId || null;
  // The person holding the room, split out of a legacy mashed cell — never the
  // raw `ahpName`, which is still the whole "A orient B" sentence.
  //
  // ⚠️ AND THEN THE SHIFT TOKEN COMES OFF. `seatCellName` strips trailing digits
  // only when the cell PARSES AS A PAIR; an ordinary annotated cell
  // ("Mike Peterson 5") comes back verbatim. Written into `orienteeName` it is
  // never cleaned again — `secondProviderOf` splits the cell only when
  // `orienteeName` is ABSENT — so "Mike Peterson 5" would become a human being's
  // name on the card, the bar, the roster and the break board, and every
  // name-key join would then miss them. The code is not lost: it travels in
  // `orienteeShiftCode`, the field that means it.
  const seatPerson = splitNameAndShift(seatCellName(row)).name;
  if (!seatId || !seatPerson) return null;

  // ⚠️ A BORROWED HOUR IS NOT PROMOTED TO A STATED ONE. `orienteeShiftInherited`
  // means the sheet printed NO number beside the trainee and the importer took the
  // preceptor's ("no number assume is 3pm"), which every surface marks "(assumed)".
  // There is no `ahpShiftInherited` for the seat, so carrying that code up would
  // launder a guess into a fact about the person now holding the room — the same
  // shape of fabrication as printing a departed CRNA's hour. Absence has to look
  // like absence, which is this module's own rule for `recordedDepartTime`.
  const inheritedHour = Boolean(row?.orienteeShiftInherited);

  return {
    ahpId: sp.id,
    ahpName: sp.name,
    ahpShiftCode: inheritedHour ? null : (sp.shiftCode ?? null),
    ahpDepartTime: recordedDepartTime(row, { second: true }),
    ...secondProviderFields({
      id: seatId,
      name: seatPerson,
      shiftCode: row?.ahpShiftCode ?? null,
      departTime: recordedDepartTime(row),
      role: sp.role,
      // ⚠️ THE BORROWED-HOUR FLAG BELONGS TO THE SLOT, NOT THE PERSON. It means
      // "the sheet printed no number beside the TRAINEE, so we took the
      // preceptor's". The person moving down is that preceptor, whose code the
      // sheet really did state — carrying the flag over would print "(assumed)"
      // against a recorded hour.
      shiftInherited: false,
    }),
  };
}

// A `buildTimeline` CASE is not an assignment row: it carries the resolved
// second person on `case.orientee` and not (today) the raw `orientee*` fields
// that `secondProviderOf` reads. Lift them back so the ONE resolver still
// answers, instead of this file growing its own copy of the question.
//
// A no-op the day buildTimeline passes the raw fields straight through, and a
// no-op for every single-provider case.
export function asAssignmentRow(caseItem) {
  if (!caseItem) return caseItem;
  const name = String(caseItem.orientee?.name || "").trim();
  if (!name) return caseItem;
  if (String(caseItem.orienteeName || "").trim()) return caseItem;
  return { ...caseItem, orienteeId: caseItem.orientee.id || null, orienteeName: name };
}

// Is `person` the SECOND provider described by `sp` (a `secondProviderOf`
// result)?
//
// ⚠️ THIS IS THE QUESTION A PAIRED BAR FORCES. The quick-actions menu opens from
// either name on a two-lane bar and is handed the SAME row both times, so
// without asking this it resolved the second provider to the person who had just
// been clicked: the menu offered "Swap Kayla Gibbons for somebody else beside
// Kayla Gibbons", and its write would have replaced her.
//
// ⚠️ THREE KEYS, IN ORDER, AND THE NAME LAST. A second provider resolved to a
// real staff record matches on `id`. One the importer never resolved carries the
// synthetic `sp:` key that `secondProviderMember` hands the roster — and that is
// the id every surface passes back for her, so it must match too. A legacy row
// that resolved neither leaves only a name, and a missing id is not an absent
// person.
//
// ⚠️ `person.key` IS READ ALONGSIDE `person.id` because a caller that refuses to
// pass a synthetic id AS an id — the chart's name trigger now does, so no write
// can pick it up by accident — still has to be recognisable as this person.
export function matchesSecondProvider(person, sp) {
  if (!person || !sp) return false;
  const pid = person.id || person.key;
  if (pid && (pid === sp.id || pid === sp.key)) return true;
  const a = String(person.name || "").trim().toLowerCase();
  const b = String(sp.name || "").trim().toLowerCase();
  return Boolean(a) && a === b;
}
